// Looking something up, on the map.
//
// The same static index the rest of the product reads: no geocoding
// server, nothing about what is typed in a query string, because there is
// no query string. What matters here is *which* call it makes.
//
// A half-typed address is made of tokens like "15" and "r" that occur in
// every shard on Earth, so a full search on one has to consult all of
// them: measured cold in a browser, `find("15 r")` was 4,960 HTTP
// requests across 294 of 310 shards and took the connection pool down
// with it. The suggest sidecar exists so a prefix never becomes a global
// fan-out, and an anchor is the other half — the same prefix anchored
// costs 21 requests. So: suggest while typing, always anchored, and a
// real search only on the line somebody actually picked.

import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  createPlaceLookup,
  type LatLon,
  type Place,
  type PlaceLookup,
  type PlaceSuggestion
} from "@wayfind/core";
import { config } from "../config";

let lookup: PlaceLookup | null = null;

function places(): PlaceLookup {
  lookup ??= createPlaceLookup({
    engineBaseUrl: config.engineBaseUrl,
    searchBaseUrl: config.searchBaseUrl
  });
  return lookup;
}

// One lookup in flight for the whole page. Discarding a superseded
// answer is not the same as not asking for it: the requests behind it run
// to completion regardless, and typing an address with natural pauses
// stacks them until the browser refuses to open any more.
let running = false;
let queued: { run(): Promise<PlaceSuggestion[]>; settle(value: PlaceSuggestion[] | null): void } | null = null;

async function pump(): Promise<void> {
  if (running) return;
  running = true;
  try {
    while (queued) {
      const job = queued;
      queued = null;
      try {
        job.settle(await job.run());
      } catch {
        job.settle([]);
      }
    }
  } finally {
    running = false;
  }
}

function suggestLatest(text: string, near: LatLon | null): Promise<PlaceSuggestion[] | null> {
  return new Promise(resolve => {
    queued?.settle(null);
    queued = { run: () => places().suggest(text, near), settle: resolve };
    void pump();
  });
}

// Where this browser last looked something up.
//
// Not a nicety: the anchor *is* the other half of the cost. The same
// prefix costs 766 requests unanchored and 21 anchored, because the index
// can only skip a shard when it knows the answer is not in it. A page
// opened cold has no run and no map centre to borrow, so without a
// remembered one the very first search of every session is the expensive
// kind. A wrong guess costs ordering, never correctness — the index is
// biased toward it, not restricted to it.
const ANCHOR_KEY = "wayfind.consumer.searchAnchor";

export function readAnchor(): LatLon | null {
  try {
    const raw = window.localStorage.getItem(ANCHOR_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { lat?: unknown; lon?: unknown };
    const lat = Number(parsed.lat);
    const lon = Number(parsed.lon);
    return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null;
  } catch {
    return null;
  }
}

function rememberAnchor(point: LatLon): void {
  try {
    window.localStorage.setItem(ANCHOR_KEY, JSON.stringify({ lat: point.lat, lon: point.lon }));
  } catch {
    // No storage means an unanchored first search. Slower, still right.
  }
}

export function MapSearch({
  near,
  onPick,
  onClear,
  picked
}: {
  /** Where the map is looking. Without one the index has no idea where to start. */
  near: LatLon | null;
  onPick(place: Place): void;
  onClear(): void;
  picked: Place | null;
}): JSX.Element {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PlaceSuggestion[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const listId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const text = query.trim();
    if (text.length < 3) { setResults([]); setBusy(false); return; }
    let live = true;
    setBusy(true);
    const timer = window.setTimeout(() => {
      suggestLatest(text, near ?? readAnchor())
        .then(found => {
          // null means a newer lookup replaced this one before it ran.
          // Its answer is still coming, so hold the list.
          if (!live || found == null) return;
          setResults(found);
          setHighlight(0);
          setOpen(true);
          setBusy(false);
        })
        .catch(() => { if (live) { setResults([]); setBusy(false); } });
    }, 220);
    return () => { live = false; window.clearTimeout(timer); };
  }, [query, near?.lat, near?.lon]);

  const take = useCallback((suggestion: PlaceSuggestion) => {
    setBusy(true);
    void places()
      .resolve(suggestion, near ?? readAnchor())
      .then(place => {
        setBusy(false);
        if (!place || place.lat == null || place.lon == null) return;
        rememberAnchor({ lat: place.lat, lon: place.lon });
        onPick(place);
        setQuery(place.label);
        setResults([]);
        setOpen(false);
        inputRef.current?.blur();
      })
      .catch(() => setBusy(false));
  }, [onPick, near?.lat, near?.lon]);

  return (
    <div className="mapsearch">
      <div className="mapsearch__bar">
        <svg className="mapsearch__glass" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="6.5" />
          <line x1="16" y1="16" x2="21" y2="21" />
        </svg>
        <input
          ref={inputRef}
          className="mapsearch__input"
          type="text"
          role="combobox"
          aria-expanded={open && results.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label="Search for a place"
          autoComplete="off"
          placeholder="Search a street or a place"
          value={query}
          onChange={event => { setQuery(event.target.value); setOpen(true); }}
          onFocus={() => { if (results.length) setOpen(true); }}
          onKeyDown={event => {
            if (event.key === "Escape") { setOpen(false); return; }
            if (!results.length) return;
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setHighlight(current => (current + 1) % results.length);
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setHighlight(current => (current - 1 + results.length) % results.length);
            } else if (event.key === "Enter") {
              event.preventDefault();
              const chosen = results[highlight];
              if (chosen) take(chosen);
            }
          }}
        />
        {query ? (
          <button
            type="button"
            className="mapsearch__clear"
            aria-label="Clear the search"
            onClick={() => { setQuery(""); setResults([]); setOpen(false); onClear(); inputRef.current?.focus(); }}
          >
            ×
          </button>
        ) : null}
      </div>

      {open && results.length ? (
        <ul className="mapsearch__list" id={listId} role="listbox">
          {results.map((suggestion, index) => (
            <li key={`${suggestion.text}-${index}`} role="option" aria-selected={index === highlight}>
              <button
                type="button"
                className="mapsearch__item"
                aria-selected={index === highlight}
                onMouseEnter={() => setHighlight(index)}
                onClick={() => take(suggestion)}
              >
                {suggestion.text}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {picked && !open ? (
        <div className="mapsearch__picked">
          <span className="mapsearch__pickedLabel">{picked.label}</span>
          <span className="mapsearch__pickedNote">
            Read out of the map index on this device. Nothing was sent anywhere.
          </span>
        </div>
      ) : null}

      {busy ? <span className="mapsearch__busy">Looking…</span> : null}
    </div>
  );
}
